import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import useAsyncEffect from "use-async-effect";

import { Moon } from "../Misc/Moon";
import { HexStateContext, getGlobalState } from "../State/Context";
import { ActionType, hexHeight, hexWidth } from "../State/State";
import { getAltitude } from "../utils/misc";
import { imgTileFromCoords, isCrawler } from "../utils/settings";
import { useLowPowerMode } from "../utils/useLowPowerMode";
import { SingleSpark } from "./SingleSpark";
import HexagenVideo from "./HexagenVideo";
import { useSparkAnimation } from "./hooks/useSparkAnimation";
import { isHexagonNew } from "./constants/sparkConfig";
import { fetchHexImage } from "./hexImageLoader";
import { requestLoadOrder, reportLoadComplete, GRID_CHANGED_MSG } from "./hexLoadOrder";
import { reportHexTileLoaded, scheduleRevokeBlob } from "./hexTileBatch";
import { acquireDecodeSlot, releaseDecodeSlot } from "./hexDecodeQueue";
import { hexDebugLog } from "../utils/videoDebugLog";

const HEX_WIDTH = 256;
const HEX_HEIGHT = 256;
const EPOCH_OFFSET = 1700000000;

export interface HexagonProps {
  x: number;
  y: number;
  /** Ring index: 0 = center, 1 = first ring (6 hexes), etc. All hexes in a ring load in parallel after previous ring completes. */
  loadRingIndex?: number;
  /** Grid generation id; when it changes, in-flight load is ignored. */
  loadGridId?: number;
}



export const getGenerationPenalty = (neighboursNumber: number): number => {
  switch (neighboursNumber) {
    case 1:
      return 350;
    case 2:
      return 200;
    case 3:
      return 100;
    case 4:
      return 50;
    default:
      return 0;
  }
};

export const findNeighboursNumber = (
  x: number,
  y: number,
  booleanMap: { [key: string]: boolean }
): number => {
  const evenRowGap = y % 2 === 0 ? 1 : 0;
  const neighbors = [
    { x: x - evenRowGap, y: y - 1 },
    { x: x + (1 - evenRowGap), y: y - 1 },
    { x: x - 1, y },
    { x: x + 1, y },
    { x: x - evenRowGap, y: y + 1 },
    { x: x + (1 - evenRowGap), y: y + 1 },
  ];

  let neighboursCount = 0;

  for (const neighbor of neighbors) {
    const key = `${neighbor.x}-${neighbor.y}`;
    if (booleanMap[key]) {
      neighboursCount++;
    }
  }

  return neighboursCount;
};

export const Hexagon: React.FC<HexagonProps> = ({ x, y, loadRingIndex = 0, loadGridId = 0 }) => {
  const { state, dispatch } = useContext(HexStateContext);

  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);
  const [isNewHexagon, setIsNewHexagon] = useState<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const pendingBlobURLRef = useRef<string | null>(null);
  const decodeSlotHeldRef = useRef(false);
  const cacheKey = `${x}-${y}`;
  const cached = state.hexTileCache[cacheKey];
  const isEmpty = cached?.isEmpty ?? true;
  const zIndex = cached?.z ?? getAltitude(x, y);
  const displayURL = cached && !cached.isEmpty ? cached.objectURL : "";
  /** Skip re-fetch when pan/zoom only changes gridId; we already have content to show. */
  const contentReadyRef = useRef<boolean>(false);
  const ringRef = useRef({ loadRingIndex: 0, loadGridId: 0 });
  const currentRunIdRef = useRef(-1);
  const previousRunIdRef = useRef(-1);
  const lastReportedRunIdRef = useRef(-1);
  const loggedOrdinaryMountRef = useRef(false);

  const imgURL = imgTileFromCoords(x, y, state.highQuality);
  const altitude = getAltitude(x, y);
  const altitudeClass = `hex-bg${altitude}`;
  const evenGap = y % 2 ? hexWidth / 2 : 0;
  const isDragging = state.isWorldDragging;
  const neighboursNumber = findNeighboursNumber(x, y, state.booleanMap);

  const isNowGenerating = state.generatingNowHexs.some(
    (hex) => hex.x === x && hex.y === y
  );

  const isNowRegenerating = state.regeneratingNowHexs.some(
    (hex) => hex.x === x && hex.y === y
  );

  const canGenerateWithoutNeighbors = (state.player?.points || 0) > 100000;
  const canInteract = neighboursNumber > 0 || canGenerateWithoutNeighbors;
  const displayNeighbours = canGenerateWithoutNeighbors && neighboursNumber === 0 ? 1 : neighboursNumber;

  const penaltyText =
    !canInteract
      ? ""
      : `${getGenerationPenalty(displayNeighbours)} pts`;
  const coordsOpacity = canInteract ? "opacity-90" : "opacity-45";
  const coordsTextColor = altitude < 3 ? "text-blue-300" : "text-black";

  const { showSpark, sparkKey, handleSparkAnimationEnd } = useSparkAnimation(isNewHexagon, isEmpty);

  const imageStyles: React.CSSProperties = displayURL
    ? {
        backgroundImage: `url(${displayURL})`,
        backgroundSize: "cover",
      }
    : {};
  const lowPower = useLowPowerMode();
  const hasVideo =
    (state.hasVideo || []).some((h) => h.x === x && h.y === y) ||
    (state.selectedHexInfo?.x === x && state.selectedHexInfo?.y === y && state.selectedHexInfo?.videoStatus === "READY");
  const showVideo = hasVideo && !lowPower;
  if (hasVideo && !showVideo) {
    console.log("[hex-video] Hexagon video blocked by lowPower", { x, y, hasVideo, lowPower, showVideo });
  }

  const onClickOnHex = () => {
    if (isNowGenerating || isNowRegenerating) return;
    if (!isDragging) {
      dispatch({ type: ActionType.SELECT_HEX, x, y });
      if (state.player) {
        if (isEmpty) {
          dispatch({ type: ActionType.TOGGLE_HEX_GENERATE_POPUP });
        } else {
          dispatch({ type: ActionType.OPEN_HEX_INFO_POPUP });
        }
      } else {
        dispatch({ type: ActionType.TOGGLE_LOGIN_POPUP });
      }
    }
  };

  useEffect(() => {
    return () => {
      if (pendingBlobURLRef.current) {
        scheduleRevokeBlob(pendingBlobURLRef.current);
        pendingBlobURLRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    contentReadyRef.current = false;
  }, [state.highQuality]);

  const isAbortOrViewportError = (err: unknown) =>
    err instanceof Error &&
    (err.name === "AbortError" || err.message === "viewport changed" || err.message === GRID_CHANGED_MSG || /abort/i.test(err.message));

  useEffect(() => {
    ringRef.current = { loadRingIndex, loadGridId };
  }, [loadGridId, loadRingIndex]);

  useAsyncEffect(
    async () => {
      ringRef.current = { loadRingIndex, loadGridId };
      previousRunIdRef.current = currentRunIdRef.current;
      currentRunIdRef.current += 1;
      const myRunId = currentRunIdRef.current;
      const reportOnce = () => {
        if (lastReportedRunIdRef.current !== myRunId) {
          lastReportedRunIdRef.current = myRunId;
          reportLoadComplete(ringRef.current.loadRingIndex, ringRef.current.loadGridId);
        }
      };
      const setEmptyAndLoaded = () => {
        hexDebugLog("Hexagon setEmptyAndLoaded", { x, y });
        const emptyZ = getAltitude(x, y);
        reportHexTileLoaded(x, y, { objectURL: null, z: emptyZ, isEmpty: true });
        contentReadyRef.current = true;
        reportOnce();
      };
      if (getGlobalState().hexTileCache[cacheKey] != null) {
        contentReadyRef.current = true;
        reportOnce();
        return;
      }
      // In-flight load (blob set, waiting for Image onload): don't revoke or restart on pan.
      if (pendingBlobURLRef.current) {
        return;
      }
      // Use ringRef so we get current grid if it changed before we got here (e.g. fast pan right after mount).
      const { loadRingIndex: ring, loadGridId: grid } = ringRef.current;
      if (!isCrawler()) {
        try {
          await requestLoadOrder(ring, grid);
        } catch (err) {
          if (isAbortOrViewportError(err)) {
            return; // pan/zoom cancelled this run; do not mark empty, effect may re-run
          }
          reportOnce();
          throw err;
        }
      }
      if (contentReadyRef.current) {
        reportOnce();
        return;
      }
      let res: Response;
      try {
        res = await fetchHexImage(imgURL);
      } catch (err) {
        if (isAbortOrViewportError(err)) {
          return; // pan/zoom cancelled; do not mark empty
        }
        reportOnce();
        setEmptyAndLoaded();
        return;
      }
      const emptyZ = getAltitude(x, y);
      if (!res.ok) {
        setEmptyAndLoaded();
        return;
      }

      const mtimeHeader = res.headers.get("x-amz-meta-mtime");
    let epochSec = 0;
    if (mtimeHeader) {
      const n = Number(mtimeHeader.trim());
      if (!isNaN(n)) epochSec = Math.floor(n);
    }
    if (epochSec === 0) {
      const lm = res.headers.get("last-modified");
      if (lm) {
        const t = lm.trim();
        if (/^\d+$/.test(t)) {
          const n = Number(t);
          if (!isNaN(n)) epochSec = n;
        } else {
          const d = Date.parse(t);
          if (!isNaN(d)) epochSec = d / 1000;
        }
      }
    }
    const z = Math.floor(epochSec) - EPOCH_OFFSET;
    setIsNewHexagon(isHexagonNew(epochSec));

    let blob: Blob;
    try {
      blob = await res.blob();
    } catch (err) {
      if (isAbortOrViewportError(err)) {
        return; // pan/zoom cancelled; do not mark empty
      }
      reportOnce();
      setEmptyAndLoaded();
      return;
    }
    const objectURL = URL.createObjectURL(blob);
    pendingBlobURLRef.current = objectURL;

    await acquireDecodeSlot();
    decodeSlotHeldRef.current = true;
    const imgObj = new Image();
    let imageSettled = false;
    const IMAGE_LOAD_TIMEOUT_MS = 6000;
    const imageTimeoutId = window.setTimeout(() => {
      if (imageSettled) return;
      imageSettled = true;
      decodeSlotHeldRef.current = false;
      releaseDecodeSlot();
      hexDebugLog("Hexagon image load timeout (blob)", { x, y });
      URL.revokeObjectURL(objectURL);
      pendingBlobURLRef.current = null;
      setEmptyAndLoaded();
    }, IMAGE_LOAD_TIMEOUT_MS);
    imgObj.onload = () => {
      if (imageSettled) return;
      imageSettled = true;
      clearTimeout(imageTimeoutId);
      decodeSlotHeldRef.current = false;
      releaseDecodeSlot();
      const isPlaceholder = imgObj.naturalWidth === 1;
      const emptyZ = getAltitude(x, y);
      if (isPlaceholder) {
        URL.revokeObjectURL(objectURL);
        pendingBlobURLRef.current = null;
        reportHexTileLoaded(x, y, { objectURL: null, z: emptyZ, isEmpty: true });
      } else {
        pendingBlobURLRef.current = null;
        reportHexTileLoaded(x, y, { objectURL, z, isEmpty: false });
      }
      contentReadyRef.current = true;
      reportOnce();
    };
    imgObj.onerror = () => {
      if (imageSettled) return;
      imageSettled = true;
      clearTimeout(imageTimeoutId);
      decodeSlotHeldRef.current = false;
      releaseDecodeSlot();
      hexDebugLog("Hexagon image onerror", { x, y });
      URL.revokeObjectURL(objectURL);
      pendingBlobURLRef.current = null;
      reportHexTileLoaded(x, y, { objectURL: null, z: getAltitude(x, y), isEmpty: true });
      contentReadyRef.current = true;
      reportOnce();
    };
    imgObj.src = objectURL;
    },
    () => {
      if (decodeSlotHeldRef.current) {
        decodeSlotHeldRef.current = false;
        releaseDecodeSlot();
      }
      const cleanedUpRunId =
        previousRunIdRef.current >= 0 ? previousRunIdRef.current : currentRunIdRef.current;
      if (lastReportedRunIdRef.current !== cleanedUpRunId) {
        reportLoadComplete(ringRef.current.loadRingIndex, ringRef.current.loadGridId);
      }
    },
    [x, y, state.highQuality, loadRingIndex, loadGridId]
  );

  useEffect(() => {
    const wasGenerated = state.generatedHexagons.find(
      (hex) => hex.x === x && hex.y === y
    );
    if (wasGenerated) {
      contentReadyRef.current = true;
      const url = imgTileFromCoords(x, y, state.highQuality) + `?${Date.now()}`;
      const newZIndex = Math.floor(wasGenerated.createdAt / 1000 - EPOCH_OFFSET);
      reportHexTileLoaded(x, y, { objectURL: url, z: newZIndex, isEmpty: false });
      setIsNewHexagon(true);
    }
  }, [state.generatedHexagons.find((hex) => hex.x === x && hex.y === y)]);

  useEffect(() => {
    const wasRegenerated = state.regeneratedHexagons.find(
      (hex) => hex.x === x && hex.y === y
    );
    if (wasRegenerated) {
      contentReadyRef.current = true;
      const url = imgTileFromCoords(x, y, state.highQuality) + `?${wasRegenerated.createdAt}`;
      const newZ = Math.floor(wasRegenerated.createdAt / 1000 - EPOCH_OFFSET);
      reportHexTileLoaded(x, y, { objectURL: url, z: newZ, isEmpty: false });
      setIsNewHexagon(true);
    }
  }, [state.regeneratedHexagons.find((hex) => hex.x === x && hex.y === y)]);

  const showVisible = !!cached || isCrawler();

  const prevVisibleRef = useRef<boolean | null>(null);
  const prevDisplayURLRef = useRef<string>("");
  useEffect(() => {
    const visibleChanged = prevVisibleRef.current !== showVisible;
    const urlChanged = prevDisplayURLRef.current !== (displayURL || "");
    if (visibleChanged || urlChanged) {
      hexDebugLog("Hexagon state", {
        x,
        y,
        showVisible,
        hadVisible: prevVisibleRef.current,
        hasDisplayURL: !!displayURL,
        hadDisplayURL: !!prevDisplayURLRef.current,
      });
      prevVisibleRef.current = showVisible;
      prevDisplayURLRef.current = displayURL || "";
    }
  });

  useEffect(() => {
    if (!hasVideo) {
      loggedOrdinaryMountRef.current = true;
      hexDebugLog("Hexagon mount", { x, y, showVisible });
    }
    return () => {
      if (loggedOrdinaryMountRef.current) hexDebugLog("Hexagon unmount", { x, y });
    };
  }, []);

  return (
    <div
      key={x + "#" + y + isNowGenerating}
      className={`hex absolute w-[256px] h-[256px] ${isEmpty ? altitudeClass : "hex-with-image"} origin-center transform transition-transform duration-300 ${
        showVisible ? "scale-100" : "scale-0"
      }`}
      style={{
        width: `${HEX_WIDTH}px`,
        height: `${HEX_HEIGHT}px`,
        left: `${x * hexWidth + evenGap}px`,
        top: `${y * hexHeight - hexHeight / 2}px`,
        zIndex: isVideoPlaying ? zIndex + 100000000 : zIndex,
        ...(showVideo ? { transform: showVisible ? 'translateZ(0) scale(1)' : 'translateZ(0) scale(0)', willChange: 'transform' } : {}),
      }}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    >
      <div
        className={`${
          !canInteract ? "pointer-events-none" : ""
        } absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center text-center`}
        onClick={onClickOnHex}
        onTouchEnd={onClickOnHex}
      >
        {isNowGenerating && <Moon />}
        {!isNowGenerating && isEmpty && isMouseOver && canInteract && (
          <div
            className={`cursor-pointer text-xs pl-1 pt-3 pb-2 ${coordsTextColor}`}
          >
            Claim me
            <br />
            for
            <br />
            {getGenerationPenalty(displayNeighbours)} pts!
          </div>
        )}
        {!isNowGenerating && isEmpty && !(isMouseOver && canInteract) && (
          <div
            className={`text-xs ${
              canInteract && "cursor-pointer"
            } p-4  ${coordsOpacity} ${coordsTextColor}`}
          >
            {canInteract ? (
              <div className={`text-[10px]`}>
                x:{x} y:{y}
              </div>
            ) : null}
            {penaltyText}
          </div>
        )}
      </div>
      <div
        className={`absolute pointer-events-none top-0 bottom-0 left-0 right-0 ${
          showVideo && isVideoPlaying ? "opacity-0" : displayURL ? "opacity-100" : "opacity-0"
        }`}
        style={imageStyles}
      ></div>

      {isNewHexagon && (
        <div className="absolute inset-0 pointer-events-none" style={{ isolation: 'isolate' }}>
          {showSpark && (
            <>
            <SingleSpark
              key={sparkKey}
              worldScale={state.worldScale}
              onAnimationEnd={handleSparkAnimationEnd}
            />
            </>
          )}
        </div>
      )}

      {isNowRegenerating && (
        <div className="absolute inset-0 flex items-center justify-center z-[20] pointer-events-none">
          <Moon />
        </div>
      )}

      {showVideo && (
        <HexagenVideo
          x={x}
          y={y}
          onPlay={() => setIsVideoPlaying(true)}
          onError={() => setIsVideoPlaying(false)}
        />
      )}
    </div>
  );
};
